// Shared SMS compose helpers: merge tags, segment counter, scheduling.

export const MERGE_TAGS = ['#firstName', '#lastName', '#fullName', '#phone', '#state', '#agentName'];

/**
 * Replace #firstName-style tags with the lead's / agent's real values.
 * Unknown "#words" are left alone so server-side tokens (#first_name, ...) still work.
 * @param {string} body
 * @param {object|null} lead
 * @param {object|null} agent
 * @returns {string}
 */
export function resolveMergeTags(body, lead, agent) {
  const first = lead?.firstName || '';
  const last = lead?.lastName || '';
  const map = {
    firstname: first,
    lastname: last,
    fullname: `${first} ${last}`.trim(),
    phone: lead?.phone || '',
    state: lead?.state || '',
    agentname: agent?.name || agent?.firstName || 'your agent',
  };
  return String(body || '').replace(/#(firstName|lastName|fullName|phone|state|agentName)\b(?:#(?![a-zA-Z_]))?/gi, (_m, key) => map[key.toLowerCase()]);
}

/** Keyword palette shown under the Send Text composer ("Available keywords:"). Resolved server-side on send. */
export const KEYWORD_TAGS = [
  '#first_name', '#last_name', '#full_name', '#email_address', '#city', '#street_address', '#state', '#zip_code',
  '#my_email', '#my_office_phone_number', '#my_forwarding_number', '#my_first_name', '#my_last_name', '#my_full_name',
  '#my_business_website', '#my_company_name', '#my_booking_link', '#my_producer_link', '#my_vcard_link',
  '#next_month_full_name', '#new_line',
];

/** Server tag that expands to the agent's Digital Business Card link. */
export const CARD_TAG = '{{digital_business_card}}';
/** Composer hard limit. */
export const SMS_MAX_CHARS = 800;
// Rough US A2P outbound price per segment; the composer only shows an estimate.
const COST_PER_SEGMENT = 0.0079;

const SITE = 'https://civixcrm.ai';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Insert `tag` at the textarea caret (or append when there is no element).
 * @param {string} value
 * @param {{selectionStart?: number, selectionEnd?: number}|null} el
 * @param {string} tag
 * @returns {{value: string, caret: number}}
 */
export function insertAtCursor(value, el, tag) {
  const start = el?.selectionStart ?? value.length;
  const end = el?.selectionEnd ?? value.length;
  return { value: `${value.slice(0, start)}${tag}${value.slice(end)}`, caret: start + tag.length };
}

/**
 * @param {object|null} agent
 * @returns {string} The agent's Digital Business Card URL ('' when none is set up).
 */
export function cardLinkOf(agent) {
  if (agent?.cardSlug) return `${SITE}/c/${agent.cardSlug}`;
  return agent?.vcardLink || (agent?._id ? `${SITE}/v/${agent._id}` : '');
}

/**
 * Body to send: appends the Digital Business Card tag when the box is ticked
 * (unless the message already carries a card keyword).
 * @param {string} body
 * @param {boolean} sendCard
 * @returns {string}
 */
export function withCardTag(body, sendCard) {
  if (!sendCard) return body;
  if (/#my_vcard_link\b|\{\{\s*digital_business_card\s*\}\}/i.test(body)) return body;
  return `${body.trimEnd()}${body.trim() ? ' ' : ''}${CARD_TAG}`;
}

/**
 * Client-side preview of the keyword set (the server stays authoritative on send).
 * @param {string} body
 * @param {object|null} lead
 * @param {object|null} agent
 * @param {Date} [now]
 * @returns {string}
 */
export function resolveKeywords(body, lead, agent, now = new Date()) {
  const [aFirst = '', ...aRest] = String(agent?.name || '').trim().split(/\s+/);
  const aLast = aRest.join(' ');
  const first = lead?.firstName || '';
  const last = lead?.lastName || '';
  const full = `${first} ${last}`.trim();
  const card = cardLinkOf(agent);
  const map = {
    first_name: first || 'there', last_name: last, full_name: full || 'there',
    email_address: lead?.email || '', city: lead?.city || '', street_address: lead?.address || '',
    state: lead?.state || '', zip_code: lead?.zip || '',
    my_email: agent?.email || '',
    my_office_phone_number: agent?.civixPhone || agent?.businessLine || agent?.phone || '',
    my_forwarding_number: agent?.forwardingPhone || agent?.phoneSettings?.cellPhone || '',
    my_first_name: aFirst, my_last_name: aLast, my_full_name: String(agent?.name || '').trim(),
    my_business_website: agent?.businessWebsite || agent?.website || '',
    my_company_name: agent?.companyName || '',
    my_booking_link: agent?.bookingSlug ? `${SITE}/schedule/${agent.bookingSlug}` : '',
    my_producer_link: agent?.producerLink || '',
    my_vcard_link: card, digital_business_card: card,
    // Quick words used in drips and preset replies (written #firstname# or #firstname)
    firstname: first || 'there', myagentname: String(agent?.name || '').trim(),
    mybookinglink: agent?.bookingSlug ? `${SITE}/schedule/${agent.bookingSlug}` : '',
    mybusinessphonenumber: agent?.civixPhone || agent?.businessLine || agent?.phone || '',
    company: agent?.companyName || 'CIVIX',
    next_month_full_name: MONTHS[(now.getMonth() + 1) % 12],
    new_line: '\n',
  };
  return String(body || '')
    .replace(/\{\{\s*([a-zA-Z0-9_-]+)\s*\}\}/g, (m, k) => (k.toLowerCase() in map ? map[k.toLowerCase()] : m))
    .replace(/#([a-zA-Z_][a-zA-Z0-9_]*)(?:#(?![a-zA-Z_]))?/g, (m, k) => (k.toLowerCase() in map ? map[k.toLowerCase()] : m));
}

/**
 * @param {number} segments
 * @returns {string} "$0" for an empty message, otherwise "$0.0079"-style.
 */
export function estimatedCost(segments) {
  return segments > 0 ? `$${(segments * COST_PER_SEGMENT).toFixed(4)}` : '$0';
}

/**
 * GSM-7 (160 / 153 per part) vs Unicode (70 / 67) segment math.
 * @param {string} text
 * @returns {{chars: number, segments: number, unicode: boolean, single: number}}
 */
export function smsSegments(text) {
  const chars = text.length;
  // eslint-disable-next-line no-control-regex
  const unicode = /[^\x00-\x7F]/.test(text);
  const [single, multi] = unicode ? [70, 67] : [160, 153];
  const segments = chars === 0 ? 0 : chars <= single ? 1 : Math.ceil(chars / multi);
  return { chars, segments, unicode, single };
}

const pad = (n) => String(n).padStart(2, '0');

/** @param {Date} d @returns {{date: string, time: string}} Local yyyy-mm-dd / HH:mm for the inputs. */
export function toInputs(d) {
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
}

/**
 * @param {Date} [now]
 * @returns {Array<{label: string, at: Date}>} Quick schedule presets.
 */
export function schedulePresets(now = new Date()) {
  const inTwoHours = new Date(now.getTime() + 2 * 3600_000);
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1); tomorrow.setHours(9, 0, 0, 0);
  const monday = new Date(now); monday.setHours(9, 0, 0, 0);
  monday.setDate(now.getDate() + (((8 - now.getDay()) % 7) || 7));
  return [
    { label: 'In 2 hours', at: inTwoHours },
    { label: 'Tomorrow 9:00 AM', at: tomorrow },
    { label: 'Monday morning', at: monday },
  ];
}

/**
 * @param {string} date - yyyy-mm-dd
 * @param {string} time - HH:mm
 * @param {Date} [now]
 * @returns {{at: Date|null, error: string}} Parsed local time, or why it is not usable (never in the past).
 */
export function parseSchedule(date, time, now = new Date()) {
  if (!date || !time) return { at: null, error: 'Pick a date and time' };
  const at = new Date(`${date}T${time}`);
  if (Number.isNaN(at.getTime())) return { at: null, error: 'Invalid date or time' };
  if (at.getTime() < now.getTime() + 60_000) return { at, error: 'Choose a time at least a minute in the future' };
  return { at, error: '' };
}
